import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import _uniq from 'lodash/uniq';
import _includes from 'lodash/includes';
import _sortBy from 'lodash/sortBy';
import { gqlWillingnessEnum as willingessEnum } from '@minecraft.graphql-types';
import Col from '@minecraft.col';
import Row from '@minecraft.row';
import { media } from '@minecraft.themes';
import { helpers } from '@minecraft.utils';
import { SCREEN_NAMES, EditProfile, AppType } from '@blocs.common';
import { useTranslation, Trans } from '@blocs.i18n';

import { AnchorButton } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

/* eslint-disable jsx-a11y/anchor-is-valid */

const sectionName = 'appearance';
const StyledRow = styled(Row)`
  margin-bottom: 1rem;
  border-bottom: 0;
  position: relative;
  ${media.sm`
    margin-bottom: 0.5rem;
    border-bottom: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  `}
`;

const StyledColLbl = styled(Col)`
  font-size: 0.85rem;
  font-weight: 600;
  padding-left: 0;
`;

const StyledColValue = styled(Col)`
  font-size: 0.85rem;
  padding-left: 0;
  ${media.sm`
    padding-left: 0.9375rem;
  `}
`;

const EditColumn = styled(Col)`
  text-align: right;
  position: absolute;
  right: 0;
  ${media.sm`
    position: static;
  `}
`;

const FadedValue = styled.div`
  color: ${(props): string => props.theme.colors.primary.grey2};
`;

const AppearanceContainer = styled.div`
  background: ${(props): string => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm`
    padding: 0.9375rem 3rem;
  `}
`;

const RecordsContainer = styled.div`
  padding-left: 1.25rem;
`;

const AppearanceWrapper = styled.div`
  background: ${(props): string => props.theme.colors.accent.ltGrey2};
  margin: 0 -1rem;
  padding: 0 1rem;
  ${media.sm`
    padding: 0 2rem;
    margin: 0 -2rem;
  `}
`;

const InfoHeading = styled.h3`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: 0.5rem;
`;

const Appearance: FC = () => {
  const { t } = useTranslation();

  const {
    appType,
    isAdmin,
    onEditClick,
    isPreview,
    fetcher: { profile },
    isPlayableAgeSectionHidden,
  } = React.useContext(ProfileViewContext);

  const isWillingToChange = (willingnessCode) => {
    const { willingness } = profile;
    const willingnessMap = _uniq(willingness.map((w) => w.code));

    return _includes(willingnessMap, willingnessCode) ? t('common:button.yes') : t('common:button.no');
  };

  if (isPreview) {
    return null;
  }

  const profilePlayableAge = profile.ageInYears
    ? t('common:range.years', {
        start: helpers.convertMonthToYear(profile.playableAgeMin),
        end: helpers.convertMonthToYear(profile.playableAgeMax),
      })
    : t('common:range.months', {
        start: profile.playableAgeMin,
        end: profile.playableAgeMax,
      });
  const noSelectionYet = t('common:form.label.noSelectionYet');
  const edit = t('common:button.edit');

  return (
    <AppearanceWrapper data-testid="profile-appearance-container">
      <AppearanceContainer id="div-appearance">
        <InfoHeading>{t('common:profile.info.appearance')}</InfoHeading>
        <EditProfile.CompoundAppearanceInfo data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}>
          {appType === AppType.Reps ? t('common:profile.appearanceInfoReps') : t('common:profile.appearanceInfo')}
        </EditProfile.CompoundAppearanceInfo>

        <RecordsContainer>
          {!isPlayableAgeSectionHidden && (
            <StyledRow data-testid="app-playable-age">
              <StyledColLbl
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-playable-age-range`}
                sm="3"
                xs="12"
              >
                {t('common:profile.info.playableAgeRange')}
              </StyledColLbl>
              <StyledColValue
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-playable-age-range`}
                sm="7"
                xs="12"
              >
                {profile.playableAgeMin && profile.playableAgeMax ? (
                  <div>{profilePlayableAge}</div>
                ) : (
                  <FadedValue>{noSelectionYet}</FadedValue>
                )}
              </StyledColValue>
              <EditColumn sm="2">
                {isAdmin ? (
                  <AnchorButton
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-playable-age-range-edit`}
                    onClick={() => {
                      onEditClick('playable-age-range');
                    }}
                  >
                    {edit}
                  </AnchorButton>
                ) : (
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-playable-age-range-edit`}
                    href={`/profile/${profile.profileId}/playable-age-range`}
                  >
                    {edit}
                  </DuoLink>
                )}
              </EditColumn>
            </StyledRow>
          )}
          <StyledRow data-testid="app-gender">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-gender`} xs="12">
              {t('common:profile.info.gender')}
            </StyledColLbl>
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-talent-is-interested-in-roles-portraying`}
              sm="2"
              xs={{ size: 'auto', offset: 1 }}
            >
              {t('common:profile.genderAppearance.title')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-is-interested-in-roles-portraying`}
              sm={{ size: 7, offset: 0 }}
              xs={{ size: 'auto', offset: 1 }}
            >
              {profile.genderAppearances && profile.genderAppearances.length > 0 ? (
                <div>{profile.genderAppearances.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-additional-identifies`}
              sm="2"
              xs={{ size: 11, offset: 1 }}
            >
              {t('common:profile.info.additionallyIdentifiesAs')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-additional-identifies`}
              sm={{ size: 7, offset: 0 }}
              xs={{ size: 'auto', offset: 1 }}
            >
              {profile.profileGenderIdentities && profile.profileGenderIdentities.length > 0 ? (
                <div>{profile.profileGenderIdentities.map((e) => e.genderIdentity.name).join(', ')}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-gender-edit`}
                  onClick={() => {
                    onEditClick('gender-appearance');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-gender-edit`}
                  href={`/profile/${profile.profileId}/gender-appearance`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="app-ethnic">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-ethnic-appearance`}
              sm="3"
              xs="12"
            >
              {t('common:profile.info.ethnicAppearance')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-ethnic-appearance`} sm="7" xs="12">
              {profile.ethnicAppearances && profile.ethnicAppearances.length > 0 ? (
                <div>
                  {_sortBy(profile.ethnicAppearances, (e) => e.order).map((e) => (
                    <React.Fragment key={e.id}>
                      {e.name} {e.order === 0 && ` ${t('common:profile.info.primary')}`} <br />
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-ethnic-appearance-edit`}
                  onClick={() => {
                    onEditClick('ethnic-appearance');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-ethnic-appearance-edit`}
                  href={`/profile/${profile.profileId}/ethnic-appearance`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="app-hair-color">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-hair-color`} sm="3" xs="12">
              {t('common:profile.info.hairColor')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-color`} sm="7" xs="12">
              {profile.hairColor && profile.hairColor.name ? (
                <div> {profile.hairColor.name}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-willing-to-change-color`}
              sm="2"
              xs={{ size: 11, offset: 1 }}
            >
              {t('common:profile.info.willingToChangeColor')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-willing-to-change-color`}
              sm={{ size: 7, offset: 0 }}
              xs={{ size: 11, offset: 1 }}
            >
              {isWillingToChange(willingessEnum.OTHER_COLOR_HAIR)}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-color-edit`}
                  onClick={() => {
                    onEditClick('hair-color');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-color-edit`}
                  href={`/profile/${profile.profileId}/hair-color`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-hair-style">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-hair-length-style`}
              sm="3"
              xs="12"
            >
              {t('common:profile.info.hairLengthStyle')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-length-style`} sm="7" xs="12">
              {profile.hairStyle && profile.hairStyle.name ? (
                <div> {profile.hairStyle.name}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-willing-to-cut-hair`}
              sm="2"
              xs={{ size: 11, offset: 1 }}
            >
              {t('common:profile.info.willingToCutHair')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-willing-to-cut-hair`}
              sm={{ size: 7, offset: 0 }}
              xs={{ size: 11, offset: 1 }}
            >
              {isWillingToChange(willingessEnum.OTHER_CUT_HAIR)}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-length-style-edit`}
                  onClick={() => {
                    onEditClick('hair-length');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hair-length-style-edit`}
                  href={`/profile/${profile.profileId}/hair-length`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-facial-hair">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-facial-hair`} sm="3" xs="12">
              {t('common:profile.info.facialHair')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-facial-hair`} sm="7" xs="12">
              {profile.facialHair && profile.facialHair.length > 0 ? (
                <div>{profile.facialHair.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-willing-to-grow-shave`}
              sm="2"
              xs={{ size: 11, offset: 1 }}
            >
              {t('common:profile.info.willingToGrowShave')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-willing-to-grow-shave`}
              sm={{ size: 7, offset: 0 }}
              xs={{ size: 11, offset: 1 }}
            >
              {isWillingToChange(willingessEnum.OTHER_GROW_OR_SHAVE_FACIAL_HAIR)}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-facial-hair-edit`}
                  onClick={() => {
                    onEditClick('facial-hair');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-facial-hair-edit`}
                  href={`/profile/${profile.profileId}/facial-hair`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-eye-color">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-eye-color`} sm="3" xs="12">
              {t('common:profile.info.eyeColor')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-eye-color`} sm="7" xs="12">
              {profile.eyeColor && profile.eyeColor.name ? (
                <div> {profile.eyeColor.name}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-eye-color-edit`}
                  onClick={() => {
                    onEditClick('eye-color');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-eye-color-edit`}
                  href={`/profile/${profile.profileId}/eye-color`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-tattoo">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-tattoos`} sm="3" xs="12">
              {t('common:profile.info.tattoos')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-tattoos`} sm="7" xs="12">
              {profile.tattoo && profile.tattoo.name ? (
                <div> {profile.tattoo.name}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
              <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-tattoos-note`}>{profile.tattooNote}</div>
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-tattoos-edit`}
                  onClick={() => {
                    onEditClick('tattoos');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-tattoos-edit`}
                  href={`/profile/${profile.profileId}/tattoos`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-piercings">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-piercings`} sm="3" xs="12">
              <Trans i18nKey="common:profile.info.piercings" />
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-piercings`} sm="7" xs="12">
              {profile.profilePiercings && profile.profilePiercings.length > 0 ? (
                <div>{profile.profilePiercings.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
              <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-piercings-note`}>
                {profile.piercingNote}
              </div>
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-piercings-edit`}
                  onClick={() => {
                    onEditClick('piercings');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-piercings-edit`}
                  href={`/profile/${profile.profileId}/piercings`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-multiples">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-identical-twin-triplet`}
              sm="3"
              xs="12"
            >
              {t('common:profile.info.identicalTwinTriplet')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-identical-twin-triplet`}
              sm="7"
              xs="12"
            >
              {profile.multipleBirth && profile.multipleBirth.name ? (
                <div> {profile.multipleBirth.name}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-identical-twin-triplet-edit`}
                  onClick={() => {
                    onEditClick('multiple-birth');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-identical-twin-triplet-edit`}
                  href={`/profile/${profile.profileId}/multiple-birth`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>

          <StyledRow data-testid="app-uniques">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-unique-attributes-conditions`}
              sm="3"
              xs="12"
            >
              {t('common:profile.info.uniqueAttributesConditions')}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-unique-attributes-conditions`}
              sm="7"
              xs="12"
            >
              {profile.uniqueAttributes && profile.uniqueAttributes.length > 0 ? (
                <div>{profile.uniqueAttributes.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{noSelectionYet}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-unique-attributes-conditions-edit`}
                  onClick={() => {
                    onEditClick('unique-attributes-conditions');
                  }}
                >
                  {edit}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-unique-attributes-conditions-edit`}
                  href={`/profile/${profile.profileId}/unique-attributes-conditions`}
                >
                  {edit}
                </DuoLink>
              )}
            </EditColumn>
          </StyledRow>
        </RecordsContainer>
      </AppearanceContainer>
    </AppearanceWrapper>
  );
};

export default Appearance;
